import { connect } from 'react-redux';
import { get, head, isEmpty, set, uniq, keyBy, orderBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal, closeGenericModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import { CATEGORY_TYPES_OBJECT, getPropertyNoneValue } from '@commons/constants/categoryTypes';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import {
  ENUM_CUSTOM_FILTERS,
  getColumnsAdvancedFilters,
  getCustomFilterIsStrategic,
  getCustomFilterMultipleStatuses,
  getCustomFilterSingleCategory,
  getCustomFilterSingleSubCategory,
} from '@commons/utils/filtersFetches';
import { formatNumber } from '@commons/DisplayNumber';
import {
  getClientStoreNameTranslation,
  getFormattedCurrencyName,
} from '@commons/utils/translations';
import { getFilteredStoresWithSelectedGroups } from '@commons/ExportModalWithStoresAndPeriod/utils';
import { getUniqueGroupsOfStores } from '@commons/utils/groups';
import { getUserTimezone } from '@commons/utils/date';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { NestedList, SearchBar, Tooltip, Button } from '@commons/utils/styledLibraryComponents';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import DisplayNumber from '@commons/DisplayNumber';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import StoresWithActiveProperty from '@commons/DropdownItems/StoresWithActiveProperty';

import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';

import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

import { supplier as supplierService } from '@services/supplier';
import clientService from '@services/client';
import orderService from '@services/order';

import { CHOICES_DROPDOWN_ACTIVE } from '@admin/utils/DropdownItems';

import ProductionAnalyticsToggle from '@src/routes/prod/components/AnalyticsContainer/components/ProductionAnalyticsToggle';

import DeepsightFiltersButton from '@orders/components/FilterButton';

import { fetchGroupsByStoreIds } from '../utils/fetches';
import { getActions } from '../utils/getActions';

import {
  Container,
  ContentContainer,
  FlexedGapContainer,
  InfoBoxLabel,
  SidePaddedGapContainer,
  TurnoverContainer,
  KPIsContainer,
} from './styledComponents';
import {
  exportOrderSPAnalyticsData,
  getAnalyticsBySupplierProductExportModalConfig,
} from './export';
import { getListHeaders, METRIC_KEYS, METRICS } from './config';

const OrderAnalyticsSupplierProducts = (props) => {
  const {
    client: { clientId, storeName, defaultTimezone },
    match,
    stores,
    showErrorMessage,
    currency,
    openGenericModal,
    refreshGenericModal,
    closeGenericModal,
    pageLoading,
    pageLoaded,
  } = props;

  const path = get(match, 'path');
  const userTimezone = getUserTimezone();
  const translatedStoreName = getClientStoreNameTranslation(storeName, false).toLowerCase();

  const yesterday = moment.tz(userTimezone).subtract(1, 'days').endOf('day');

  const [selectedMetric, setSelectedMetric] = useState(head(METRICS));

  const [isLoading, setIsLoading] = useState(true);

  const [headers, setHeaders] = useState(getListHeaders(selectedMetric));
  const [data, setData] = useState([]);
  const [totalRow, setTotalRow] = useState([]);
  const [turnover, setTurnover] = useState(null);
  const [actions, setActions] = useState([]);

  /** Filters **/
  const [searchInput, setSearchInput] = useState('');
  const [categoriesToChoose, setCategoriesToChoose] = useState([]);
  const [subCategoriesToChoose, setSubCategoriesToChoose] = useState([]);

  const [suppliers, setSuppliers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [formattedStores] = useState(
    stores.map((store) => ({
      ...store,
      renderValue: () => StoresWithActiveProperty(store),
    })),
  );
  const [storesWithSuppliers, setStoresWithSuppliers] = useState([]);

  const [filters, setFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState();
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [isStrategic, setIsStrategic] = useState(true);
  const [statuses, setStatuses] = useState(CHOICES_DROPDOWN_ACTIVE());
  const [receivedOrderPercentage, setReceivedOrderPercentage] = useState(null);

  const periodPickerState = usePeriodDatePickerState(
    moment.tz(yesterday, userTimezone).subtract(30, 'days').startOf('day'),
    yesterday,
    userTimezone,
  );

  // Export modal states
  const [exportStores, setExportStores] = useState([]);
  const [exportSelectedStores, setExportSelectedStores] = useState(null);
  const [exportGroups, setExportGroups] = useState([]);
  const [exportSelectedGroups, setExportSelectedGroups] = useState([]);
  const [exportPeriod, setExportPeriod] = useState(null);

  const customFilterMultipleStatuses = getCustomFilterMultipleStatuses(statuses, setStatuses);
  const customFilterIsStrategic = getCustomFilterIsStrategic(isStrategic, setIsStrategic);
  const customFilterSingleCategory = getCustomFilterSingleCategory(
    categoriesToChoose,
    selectedCategory,
    setSelectedCategory,
  );
  const customFilterSingleSubCategory = getCustomFilterSingleSubCategory(
    subCategoriesToChoose,
    selectedSubCategory,
    setSelectedSubCategory,
  );

  useEffect(() => {
    if (isEmpty(stores)) {
      return;
    }

    const storeIds = stores.map(({ id }) => id);

    (async function loadData() {
      const clientSuppliers = await supplierService.getSuppliersOfClient();

      const { storeIdsWithSuppliers, suppliersWithStoresMapping } = clientSuppliers.reduce(
        (result, supplier) => {
          const { storesMapped } = supplier;

          if (storesMapped.length) {
            result.suppliersWithStoresMapping.push(supplier);

            const mappedStoreIds = storesMapped.map(({ storeId }) => storeId);

            result.storeIdsWithSuppliers = result.storeIdsWithSuppliers.concat(mappedStoreIds);
          }

          return result;
        },
        { storeIdsWithSuppliers: [], suppliersWithStoresMapping: [] },
      );

      const storeIdsMappedToSuppliers = uniq(storeIdsWithSuppliers).filter((storeId) =>
        storeIds.includes(storeId),
      );

      const storesWithSuppliers = formattedStores.filter(({ id }) =>
        storeIdsMappedToSuppliers.includes(id),
      );

      const activeStores = storesWithSuppliers.filter(({ active }) => active);

      const storesWithSuppliersIds = storesWithSuppliers.map(({ id }) => id);

      const [fetchedGroups, { categories, subCategories }] = await Promise.all([
        fetchGroupsByStoreIds(storesWithSuppliersIds, showErrorMessage),
        clientService.getCategoriesAndSubCategories(
          CATEGORY_TYPES_OBJECT.SUPPLIER_PRODUCT,
          false, // withAssociatedItems
          true, // withTranslations
        ),
      ]);

      setGroups(fetchedGroups);

      const formattedCategories = categories.map((category) => ({
        id: category.id,
        name: category.name || getPropertyNoneValue(),
      }));

      const formattedSubCategories = subCategories.map((subCategory) => ({
        id: subCategory.id,
        name: subCategory.name || getPropertyNoneValue(),
      }));

      setCategoriesToChoose(formattedCategories);
      setSubCategoriesToChoose(formattedSubCategories);

      setSuppliers(suppliersWithStoresMapping);
      setSelectedSuppliers(suppliersWithStoresMapping);

      setStoresWithSuppliers(storesWithSuppliers);
      setSelectedStores(activeStores);

      const updatedFilters = {
        stores: {
          activeList: storesWithSuppliers,
          list: storesWithSuppliers,
          selectedStores: activeStores,
        },
        groups: !isEmpty(fetchedGroups)
          ? {
              list: fetchedGroups,
              selectedGroups,
            }
          : null,
        suppliers: !isEmpty(suppliersWithStoresMapping)
          ? {
              list: suppliersWithStoresMapping,
              activeList: suppliersWithStoresMapping,
              selectedSuppliers: suppliersWithStoresMapping,
            }
          : null,
        [ENUM_CUSTOM_FILTERS.TOGGLE_IS_STRATEGIC]: {
          ...customFilterIsStrategic,
        },
        [ENUM_CUSTOM_FILTERS.MULTIPLE_SELECT_STATUSES]: {
          ...customFilterMultipleStatuses,
        },
        [ENUM_CUSTOM_FILTERS.SINGLE_SELECT_CATEGORY]: !isEmpty(formattedCategories)
          ? {
              ...set(customFilterSingleCategory, 'list', [...formattedCategories]),
            }
          : null,
        [ENUM_CUSTOM_FILTERS.SINGLE_SELECT_SUB_CATEGORY]: !isEmpty(formattedSubCategories)
          ? {
              ...set(customFilterSingleSubCategory, 'list', [...formattedSubCategories]),
            }
          : null,
      };

      setFilters(updatedFilters);
      setApplyFilters(true);

      // Initialize export modal states
      const uniqGroups = getUniqueGroupsOfStores(storesWithSuppliers);
      setExportGroups(uniqGroups);
      setExportSelectedGroups(uniqGroups);
    })();
  }, []); // No need to listen on redux stores, it causes multiple useless call and re-rendering

  useEffect(() => {
    setActions(getActions(translatedStoreName, handleGlobalExport, openByStoreExportModal));
  }, [
    exportSelectedGroups,
    selectedStores,
    periodPickerState.startDate,
    periodPickerState.endDate,
  ]);

  useEffect(() => {
    if (!exportSelectedGroups.length) {
      return;
    }

    const filteredStores = getFilteredStoresWithSelectedGroups(
      exportSelectedGroups,
      storesWithSuppliers,
    );

    // Initial list of stores to export
    setExportStores(filteredStores);

    // Selected stores to export
    setExportSelectedStores(filteredStores);
  }, [exportSelectedGroups]);

  useEffect(() => {
    if (applyFilters) {
      reloadData();
    }
  }, [applyFilters]);

  useEffect(() => {
    if (periodPickerState.startDate && periodPickerState.endDate) {
      reloadData();
    }
  }, [periodPickerState.startDate, periodPickerState.endDate]);

  useEffect(() => {
    setHeaders(getListHeaders(selectedMetric));

    if (selectedMetric.key === METRIC_KEYS.CURRENCY) {
      const total = makeTotalRow(data);

      setTotalRow(total);
    } else {
      setTotalRow([]);
    }
  }, [data, selectedMetric]);

  useEffect(() => {
    if (!exportSelectedStores) {
      return;
    }

    openByStoreExportModal(true);
  }, [exportStores, exportSelectedStores, exportPeriod]);

  const makeTotalRow = (data) => {
    const total = data.reduce(
      (totals, productAnalytics) => {
        totals.orderedVolume += productAnalytics.orderedVolume.inCurrency;
        totals.invoicedVolume += productAnalytics.invoicedVolume.inCurrency;
        totals.receivedVolume += productAnalytics.receivedVolume.inCurrency;
        totals.receivedDiff += productAnalytics.receivedDiff.inCurrency;
        return totals;
      },
      {
        orderedVolume: 0,
        invoicedVolume: 0,
        receivedVolume: 0,
        receivedDiff: 0,
      },
    );

    const totalServiceRate = total.orderedVolume
      ? (total.receivedVolume / total.orderedVolume) * 100
      : null;

    return [
      {
        name: i18next.t('GENERAL.ANALYTICS_LABEL_TOTAL'),
        supplier: { name: '- ' },
        orderedVolume: { inCurrency: total.orderedVolume },
        invoicedVolume: { inCurrency: total.invoicedVolume },
        receivedVolume: { inCurrency: total.receivedVolume },
        receivedDiff: { inCurrency: total.receivedDiff },
        serviceRate: totalServiceRate,
      },
    ];
  };

  const reloadData = () => {
    getAnalyticsData();
    getTurnoverData();
  };

  const getTurnoverData = async () => {
    const startDate = moment
      .tz(periodPickerState.startDate, userTimezone)
      .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
    const endDate = moment
      .tz(periodPickerState.endDate, userTimezone)
      .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
    const selectedStoresIds = selectedStores.map(({ id }) => id);

    try {
      const turnoverByStoreId = await orderService.getTurnoverByStoreIdsAndDates(
        selectedStoresIds,
        startDate,
        endDate,
      );
      setTurnover(<DisplayNumber number={turnoverByStoreId['TOTAL']} withoutDecimals={true} />);
    } catch {
      setTurnover('-');
    }
  };

  const getAnalyticsData = async () => {
    if (isEmpty(stores) || isEmpty(suppliers)) {
      return;
    }
    setIsLoading(true);

    try {
      const categoryIds = selectedCategory && !isStrategic ? [selectedCategory.id] : null;
      const subCategoryIds = selectedSubCategory && !isStrategic ? [selectedSubCategory.id] : null;

      const selectedSuppliersIds = selectedSuppliers.map((supplier) => supplier.id);
      const startDate = moment
        .tz(periodPickerState.startDate, userTimezone)
        .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
      const endDate = moment
        .tz(periodPickerState.endDate, userTimezone)
        .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
      const selectedStoresIds = selectedStores.map(({ id }) => id);

      const { analyticsBySupplierProducts, receivedPercentage } =
        await orderService.getOrdersAnalyticsBySupplierProduct(
          clientId,
          startDate,
          endDate,
          selectedStoresIds,
          selectedSuppliersIds,
          isStrategic,
          categoryIds,
          subCategoryIds,
        );

      setReceivedOrderPercentage(receivedPercentage);

      const statusesFilter = statuses.map(({ filterValue }) => filterValue);

      const analyticsFilteredByStatus = analyticsBySupplierProducts.reduce((result, analytic) => {
        const status = get(analytic, 'supplierProduct.active', false);

        if (!statusesFilter.includes(status)) {
          return result;
        }

        const formattedAnalytics = {
          ...analytic,
          supplierProduct: {
            ...analytic.supplierProduct,
            category: analytic.supplierProduct.category || getPropertyNoneValue(),
            subCategory: analytic.supplierProduct.subCategory || getPropertyNoneValue(),
          },
        };

        result.push(formattedAnalytics);

        return result;
      }, []);

      // No advanced filter to take into consideration, then early return
      if (isEmpty(advancedFilters)) {
        setData(analyticsFilteredByStatus);

        return;
      }

      const filteredDataWithAdvancedFilters = advancedFilters.reduce(
        (result, { doFilter, propertyKey, value }) => doFilter(result, propertyKey, value),
        analyticsFilteredByStatus,
      );

      setData(filteredDataWithAdvancedFilters);
    } catch {
      setData([]);
      showErrorMessage(i18next.t('ORDERS.BY_SUPPLIER_PRODUCT.FETCH_ANALYTICS_FAILURE'));
    } finally {
      setIsLoading(false);
    }
  };

  const filterBySearch = (data, searchInput) => {
    if (!searchInput) {
      return data;
    }

    return data.filter((analytic) => {
      const {
        supplierProduct: { name, sku },
      } = analytic;

      return (
        name.toLowerCase().includes(searchInput.toLowerCase()) || (sku && sku.includes(searchInput))
      );
    });
  };

  const handleGlobalExport = async () => {
    // Close export setup modal and export data
    resetExportModal();

    if (!selectedStores || !selectedStores.length) {
      return;
    }

    try {
      pageLoading();
      /** From applied filters */
      const selectedSuppliersIds = selectedSuppliers.map(({ id }) => id);
      const categoryIds = selectedCategory && !isStrategic ? [selectedCategory.id] : null;
      const subCategoryIds = selectedSubCategory && !isStrategic ? [selectedSubCategory.id] : null;

      const exportSelectedStoreIds = selectedStores.map(({ id }) => id);

      const { analyticsBySupplierProducts } =
        await orderService.getOrdersAnalyticsBySupplierProduct(
          clientId,
          periodPickerState.startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          periodPickerState.endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          exportSelectedStoreIds,
          selectedSuppliersIds,
          isStrategic,
          categoryIds,
          subCategoryIds,
        );

      const statusesFilter = statuses.map(({ filterValue }) => filterValue);

      const filteredAnalyticsToExport = analyticsBySupplierProducts.reduce((result, analytic) => {
        const status = get(analytic, 'supplierProduct.active', false);

        if (!statusesFilter.includes(status)) {
          return result;
        }

        const formattedAnalytics = {
          ...analytic,
          supplierProduct: {
            ...analytic.supplierProduct,
            category: analytic.supplierProduct.category || getPropertyNoneValue(),
            subCategory: analytic.supplierProduct.subCategory || getPropertyNoneValue(),
          },
        };

        result.push(formattedAnalytics);

        return result;
      }, []);

      const exportTotalValue = makeTotalRow(filteredAnalyticsToExport);
      const totalRowData = head(exportTotalValue) || {};

      const dateRange = {
        startDate: moment
          .tz(periodPickerState.startDate, userTimezone)
          .format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
        endDate: moment
          .tz(periodPickerState.endDate, userTimezone)
          .format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
      };

      const storeTranslations = {
        plural: getClientStoreNameTranslation(storeName, true),
        single: getClientStoreNameTranslation(storeName, false),
      };

      const sortedAnalytics = orderBy(
        filteredAnalyticsToExport,
        'orderedVolume.inCurrency',
        'desc',
      );

      exportOrderSPAnalyticsData(
        sortedAnalytics,
        {
          dateRange,
          selectedStores,
          selectedSuppliers, // From applied filters
          isStrategic, // From applied filters
          selectedCategory, // From applied filters
          selectedSubCategory, // From applied filters
          statuses, // From applied filters
        },
        storeTranslations,
        currency,
        totalRowData,
      );
    } catch {
      showErrorMessage(i18next.t('ANALYSIS.PURCHASES.FETCH_ANALYTICS_FAILURE'));
    } finally {
      pageLoaded();
    }
  };

  const openByStoreExportModal = (shouldRefreshModal = false) => {
    const params = getAnalyticsBySupplierProductExportModalConfig(
      {
        maxFutureDate: yesterday,
        initialStartDate: periodPickerState.startDate,
        initialEndDate: periodPickerState.endDate,
        exportGroups,
        exportSelectedGroups,
        setExportSelectedGroups,
        exportStores,
        exportSelectedStores,
        setExportSelectedStores,
        exportPeriod,
        setExportPeriod,
        clientStoreName: getClientStoreNameTranslation(storeName, true),
        defaultTimezone,
      },
      handleExportByStore,
      resetExportModal,
    );

    if (shouldRefreshModal) {
      refreshGenericModal(params);
      return;
    }

    openGenericModal(params);
  };

  const resetExportModal = () => {
    closeGenericModal();
    const uniqGroups = getUniqueGroupsOfStores(storesWithSuppliers);

    setExportGroups(uniqGroups);
    setExportSelectedGroups(uniqGroups);
  };

  const handleExportByStore = async (selectedStoresForExport, startDate, endDate) => {
    // Close export setup modal and export data
    resetExportModal();

    if (!selectedStoresForExport || !selectedStoresForExport.length) {
      return;
    }

    try {
      pageLoading();
      /** From applied filters */
      const selectedSuppliersIds = selectedSuppliers.map(({ id }) => id);
      const categoryIds = selectedCategory && !isStrategic ? [selectedCategory.id] : null;
      const subCategoryIds = selectedSubCategory && !isStrategic ? [selectedSubCategory.id] : null;

      const exportSelectedStoreIds = selectedStoresForExport.map(({ id }) => id);

      const { analyticsBySupplierProducts } =
        await orderService.getOrdersAnalyticsBySupplierProduct(
          clientId,
          startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          exportSelectedStoreIds,
          selectedSuppliersIds,
          isStrategic,
          categoryIds,
          subCategoryIds,
        );

      const statusesFilter = statuses.map(({ filterValue }) => filterValue);

      const storesKeyByIds = keyBy(stores, 'id');

      const filteredAnalyticsToExport = analyticsBySupplierProducts.reduce((result, analytic) => {
        const status = get(analytic, 'supplierProduct.active', false);

        if (!statusesFilter.includes(status)) {
          return result;
        }

        Object.entries(analytic.dataByStoreId).forEach(([storeId, value]) => {
          result.push({
            entityName: analytic.entityName,
            masterUnit: analytic.masterUnit,
            name: analytic.name,
            sku: analytic.sku,
            supplier: analytic.supplier,
            storeName: storesKeyByIds[storeId].name,
            orderedVolume: value.orderedVolume,
            invoicedVolume: value.invoicedVolume,
            receivedVolume: value.receivedVolume,
            serviceRate: value.serviceRate,
            receivedDiff: value.receivedDiff,
            supplierProduct: {
              ...analytic.supplierProduct,
              category: analytic.supplierProduct.category || getPropertyNoneValue(),
              subCategory: analytic.supplierProduct.subCategory || getPropertyNoneValue(),
            },
          });
        });

        return result;
      }, []);

      const dateRange = {
        startDate: moment
          .tz(startDate, userTimezone)
          .format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
        endDate: moment
          .tz(endDate, userTimezone)
          .format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
      };

      const storeTranslations = {
        plural: getClientStoreNameTranslation(storeName, true),
        single: getClientStoreNameTranslation(storeName, false),
      };

      const sortedAnalytics = orderBy(
        filteredAnalyticsToExport,
        ['name', 'orderedVolume.inCurrency', 'storeName'],
        'asc',
      );

      exportOrderSPAnalyticsData(
        sortedAnalytics,
        {
          dateRange,
          selectedStores: selectedStoresForExport,
          selectedSuppliers, // From applied filters
          isStrategic, // From applied filters
          selectedCategory, // From applied filters
          selectedSubCategory, // From applied filters
          statuses, // From applied filters
        },
        storeTranslations,
        currency,
        null,
        true, // export by store
      );
    } catch {
      showErrorMessage(i18next.t('ANALYSIS.PURCHASES.FETCH_ANALYTICS_FAILURE'));
    } finally {
      pageLoaded();
    }
  };

  useEffect(() => {
    const total = makeTotalRow(data);

    setTotalRow(total);
  }, [data]);

  return (
    <Container>
      <NavigationBreadCrumb featurePath={path} />
      <ContentContainer>
        <SidePaddedGapContainer>
          <LeftRightSplitter
            left={
              <FlexedGapContainer>
                <SearchBar
                  disabled={isLoading}
                  placeholder={i18next.t('GENERAL.SEARCH')}
                  setValue={setSearchInput}
                  value={searchInput}
                />
                <div>
                  <DeepsightFiltersButton
                    advancedFilters={advancedFilters}
                    columnsFilterList={getColumnsAdvancedFilters(headers)}
                    customMultipleDropDowns={[customFilterMultipleStatuses]}
                    customSingleDropDowns={[
                      customFilterSingleCategory,
                      customFilterSingleSubCategory,
                    ]}
                    customToggles={[customFilterIsStrategic]}
                    filters={filters}
                    groups={groups}
                    readOnly={isLoading}
                    selectedGroups={selectedGroups}
                    selectedStores={selectedStores}
                    selectedSuppliers={selectedSuppliers}
                    setAdvancedFilters={setAdvancedFilters}
                    setApplyFilters={setApplyFilters}
                    setFilters={setFilters}
                    setSelectedGroups={setSelectedGroups}
                    setSelectedStores={setSelectedStores}
                    setSelectedSuppliers={setSelectedSuppliers}
                    stores={storesWithSuppliers}
                    style={{ width: 'unset !important' }}
                    suppliers={suppliers}
                    textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
                  />
                </div>
                <PeriodDatePicker
                  disabled={isLoading}
                  endDate={periodPickerState.endDate}
                  focusedDateInput={periodPickerState.focusedDateInput}
                  maxFutureDate={yesterday}
                  setFocusedDateInput={periodPickerState.setFocusedDateInput}
                  startDate={periodPickerState.startDate}
                  timezone={userTimezone}
                  onDatesChange={periodPickerState.handleSelectedDates}
                />
                <ProductionAnalyticsToggle
                  choices={METRICS}
                  isDisabled={isLoading}
                  selectedChoice={selectedMetric}
                  setSelectedChoice={setSelectedMetric}
                />
                <KPIsContainer>
                  <TurnoverContainer>
                    <InfoBoxLabel>
                      {i18next.t('GENERAL.METRIC_TURNOVER_EX_TAX', {
                        currencyCode: currency.alphabeticCode,
                      })}
                      <Tooltip
                        text={i18next.t('GENERAL.METRIC_TURNOVER_EX_TAX_TOOLTIP', {
                          currencyName: getFormattedCurrencyName(currency.shortenedName, true),
                        })}
                      />
                    </InfoBoxLabel>
                    <div className="kpi">
                      {isLoading ? <DeepsightComponentLoader height={16} width={16} /> : turnover}
                    </div>
                  </TurnoverContainer>
                  <TurnoverContainer>
                    <InfoBoxLabel>
                      {i18next.t('ANALYSIS.PURCHASES.RECEIVED_PERCENTAGE')}
                      <Tooltip text={i18next.t('ANALYSIS.PURCHASES.RECEIVED_PERCENTAGE_TOOLTIP')} />
                    </InfoBoxLabel>
                    <div className="kpi">
                      {isLoading ? (
                        <DeepsightComponentLoader height={16} width={16} />
                      ) : (
                        `${formatNumber(receivedOrderPercentage, 2)}%`
                      )}
                    </div>
                  </TurnoverContainer>
                </KPIsContainer>
              </FlexedGapContainer>
            }
            right={
              <Button
                actions={actions}
                color={'inpulse-outline'}
                fontSize={14}
                formatText={false}
                isDisabled={isLoading}
                label={i18next.t('GENERAL.ACTIONS')}
              />
            }
          />
        </SidePaddedGapContainer>

        <NestedList
          data={filterBySearch(data, searchInput)}
          defaultOrderBy={`orderedVolume.inCurrency`}
          defaultOrderType={'desc'}
          fixedRowsData={totalRow}
          headers={headers}
          isExpandable={false}
          isLoading={isLoading}
          labelEmptyState={i18next.t('GENERAL.NO_RESULT')}
          minWidth={'978px'}
        />
      </ContentContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  stores: getSalesPointStores(state.baseReducer.stores),
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
});

const mapDispatchToProps = (dispatch) => ({
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderAnalyticsSupplierProducts);
